import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import FullReversalModal from "../FullReversalModal";
import ReturnLinesEditor, { returnLinesTotal, toReturnLineRequests, useReturnLookups, validateReturnLines } from "../ReturnLinesEditor";
import CustomerAccountModal, { fetchCustomerAccount } from "./CustomerAccountModal";
import { CustomerScopeFilters } from "../CustomerSelect";
import {
  COLLECTION_TYPES,
  CustomerPicker,
  EmptyRow,
  HIGHLIGHT_STYLES,
  HighlightLegend,
  LoadingOverlay,
  RECEIVABLES_API,
  fieldsFor,
  fmtDate,
  fmtDateTime,
  money,
  sumBy,
  todayIso,
  useReceivableLookups,
} from "./common";

const PAYMENTS_API = `${RECEIVABLES_API}/payments`;

const newBlock = (amount = "") => ({
  collectionType: "CASH",
  amount,
  referenceNumber: "",
  bankName: "",
  branchName: "",
  chequeDate: "",
  paymentDate: todayIso(),
  creditNoteKind: "ITEM_WISE",
  returnLines: [],
});

/**
 * Kinds of credit note. Item-wise: goods handed back against this bill, credited on the bill. Financial / voucher: raised
 * against the customer, added to the customer's account balance (not to this bill). Adjust: part of that balance set off
 * against this bill.
 */
const CREDIT_NOTE_KINDS = [
  { value: "ITEM_WISE", label: "Item-wise (goods returned)" },
  { value: "FINANCIAL", label: "Financial (to customer account)" },
  { value: "VOUCHER", label: "Voucher (to customer account)" },
  { value: "ACCOUNT_ADJUSTMENT", label: "Adjust from account balance" },
];

/**
 * Kinds of debit note — the opposite of the credit note kinds. Item-wise: goods supplied in addition to this bill, charged on it
 * and taken out of stock. Financial / voucher: charged to the customer's account balance (not to this bill). Adjust: an amount put
 * back on this bill and added to the customer's account balance.
 */
const DEBIT_NOTE_KINDS = [
  { value: "ITEM_WISE", label: "Item-wise (goods supplied)" },
  { value: "FINANCIAL", label: "Financial (charged to customer account)" },
  { value: "VOUCHER", label: "Voucher (charged to customer account)" },
  { value: "ACCOUNT_ADJUSTMENT", label: "Adjust to account balance" },
  // a plain amount charged on this bill (no goods, no account)
  { value: "AMOUNT", label: "Amount on this bill" },
];

const isCreditNote = (block) => block.collectionType === "CREDIT_NOTE";
const isDebitNote = (block) => block.collectionType === "DEBIT_NOTE";
const isNote = (block) => isCreditNote(block) || isDebitNote(block);
const toAccount = (block) => isNote(block) && (block.creditNoteKind === "FINANCIAL" || block.creditNoteKind === "VOUCHER");
const itemWise = (block) => isNote(block) && block.creditNoteKind === "ITEM_WISE";

/**
 * What a line takes off this bill: item-wise credit notes by their goods' value; notes to the account take nothing, and a debit
 * note adds to the bill instead.
 */
const amountOnBill = (block, invoiceItems) => {
  if (!fieldsFor(block.collectionType).amount || toAccount(block) || isDebitNote(block)) return 0;
  if (itemWise(block)) return returnLinesTotal(block.returnLines || [], invoiceItems);
  return Number(block.amount || 0);
};

/** The payment lines entered against one invoice (cash + cheque + ... in any mix). */
const CollectionBlocks = ({ invoice, blocks, onChange, banks, creditNoteReference, products, warehouses, onItemsLoaded }) => {
  const update = (index, field, value) => onChange(blocks.map((block, i) => (i === index ? { ...block, [field]: value } : block)));
  const [invoiceItems, setInvoiceItems] = useState(null);
  const [account, setAccount] = useState(null);
  const [showAccount, setShowAccount] = useState(false);
  const needsItems = blocks.some(itemWise);
  const needsAccount = blocks.some(isNote);

  useEffect(() => {
    if (!needsItems || invoiceItems) return;
    api
      .get(`/api/v1/sales-invoices/${invoice.invoiceId}`)
      .then((data) => {
        setInvoiceItems(data?.items || []);
        onItemsLoaded?.(invoice.invoiceId, data?.items || []);
      })
      .catch((error) => {
        setInvoiceItems([]);
        toast.error(error.message || "Could not load the bill's items");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsItems, invoice.invoiceId]);

  useEffect(() => {
    if (!needsAccount || account || !invoice.customerId) return;
    fetchCustomerAccount(invoice.customerId)
      .then(setAccount)
      .catch(() => setAccount({ balance: 0, entries: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsAccount, invoice.customerId]);

  const total = blocks.reduce((sum, block) => sum + amountOnBill(block, invoiceItems || []), 0);

  return (
    <div className="page-panel" style={{ marginTop: 12, padding: "14px 16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <strong>
          {invoice.invoiceNumber} — {invoice.customerName}
        </strong>
        <span className="page-subtitle">
          Balance ₹{money(invoice.balanceAmount)} · Entered ₹{money(total)}
          {total > Number(invoice.balanceAmount) ? <span className="error"> (more than balance)</span> : null}
          {invoice.customerId ? (
            <>
              {" "}·{" "}
              <button type="button" className="link-button" onClick={() => setShowAccount(true)} title="Customer account balance and credit notes"
                style={{ background: "none", border: "none", padding: 0, color: "var(--color-primary, #2563eb)", cursor: "pointer" }}>
                Account{account ? ` ₹${money(account.balance)}` : ""}
              </button>
            </>
          ) : null}
        </span>
      </div>
      {blocks.map((block, index) => {
        const f = fieldsFor(block.collectionType, creditNoteReference);
        const creditNote = isNote(block);
        const debit = isDebitNote(block);
        return (
          <div key={index} style={{ marginTop: 10 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div className="form-group">
              <label>Type</label>
              <select value={block.collectionType} onChange={(e) => update(index, "collectionType", e.target.value)}>
                {COLLECTION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            {creditNote ? (
              <div className="form-group">
                <label>{debit ? "Debit" : "Credit"} note type</label>
                <select value={block.creditNoteKind || "ITEM_WISE"} onChange={(e) => update(index, "creditNoteKind", e.target.value)}>
                  {(debit ? DEBIT_NOTE_KINDS : CREDIT_NOTE_KINDS).map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            {creditNote && itemWise(block) ? (
              <div className="form-group" style={{ width: 130 }}>
                <label>Amount</label>
                <input
                  value={money(returnLinesTotal(block.returnLines || [], invoiceItems || []))}
                  readOnly
                  title={debit ? "Value of the goods supplied below" : "Value of the goods returned below"}
                />
              </div>
            ) : f.amount ? (
              <div className="form-group" style={{ width: 130 }}>
                <label>Amount</label>
                <input type="number" min="0.01" step="0.01" value={block.amount} onChange={(e) => update(index, "amount", e.target.value)} />
              </div>
            ) : null}
            {f.reference ? (
              <div className="form-group" style={{ width: 150 }}>
                <label>{f.referenceLabel}</label>
                <input value={block.referenceNumber} maxLength={60} onChange={(e) => update(index, "referenceNumber", e.target.value)} />
              </div>
            ) : null}
            {f.instrumentDate ? (
              <div className="form-group">
                <label>{f.instrumentDateLabel}</label>
                <input type="date" value={block.chequeDate} onChange={(e) => update(index, "chequeDate", e.target.value)} />
              </div>
            ) : null}
            {f.bank ? (
              <div className="form-group" style={{ width: 170 }}>
                <label>Bank</label>
                {/* banks come from the bank master (Masters > Banks) */}
                <select value={block.bankName} onChange={(e) => update(index, "bankName", e.target.value)}>
                  <option value="">Select bank</option>
                  {banks.map((bank) => (
                    <option key={bank} value={bank}>
                      {bank}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            {f.branch ? (
              <div className="form-group" style={{ width: 150 }}>
                <label>Branch</label>
                <input value={block.branchName} onChange={(e) => update(index, "branchName", e.target.value)} />
              </div>
            ) : null}
            <div className="form-group">
              <label>Payment Date</label>
              <input type="date" value={block.paymentDate} max={todayIso()} onChange={(e) => update(index, "paymentDate", e.target.value)} />
            </div>
            <button
              type="button"
              className="secondary"
              onClick={() => onChange(blocks.filter((_, i) => i !== index))}
              disabled={blocks.length === 1}
              title="Remove this line"
            >
              ✕
            </button>
          </div>
          {creditNote && toAccount(block) ? (
            <div className="page-subtitle" style={{ marginTop: 4 }}>
              {debit
                ? `This debit note is charged to ${invoice.customerName}'s account balance — it does not raise this bill.`
                : `This credit note is raised against ${invoice.customerName} and added to the customer's account balance — it does not reduce this bill. Adjust it against bills later with "Adjust from account balance".`}
            </div>
          ) : null}
          {debit && block.creditNoteKind === "ACCOUNT_ADJUSTMENT" ? (
            <div className="page-subtitle" style={{ marginTop: 4 }}>
              The amount is added back to this bill and to the customer's account balance (now ₹{money(account?.balance ?? 0)}).
            </div>
          ) : null}
          {creditNote && !debit && block.creditNoteKind === "ACCOUNT_ADJUSTMENT" ? (
            <div className="page-subtitle" style={{ marginTop: 4 }}>
              Available on account: ₹{money(account?.balance ?? 0)}
              {Number(block.amount || 0) > Number(account?.balance ?? 0) ? <span className="error"> (more than the account balance)</span> : null}
            </div>
          ) : null}
          {creditNote && itemWise(block) ? (
            <div style={{ marginTop: 8 }}>
              <div className="page-subtitle" style={{ marginBottom: 6 }}>
                {debit
                  ? "Products supplied in addition to this bill (more of a bill item, or any other product) and the warehouse each comes out of. The stock is taken out of that warehouse and the value is added to this bill."
                  : "Products handed back on this bill (or any other product, e.g. expired stock) and the warehouse each goes into. The stock is added to that warehouse and the value is adjusted on this bill."}
              </div>
              {invoiceItems === null ? (
                <div className="page-subtitle">Loading the bill's items...</div>
              ) : (
                <ReturnLinesEditor
                  key={debit ? "debit" : "credit"}
                  debit={debit}
                  prefillInvoiceItems
                  lines={block.returnLines || []}
                  onChange={(lines) => update(index, "returnLines", lines)}
                  products={products}
                  warehouses={warehouses}
                  invoiceItems={invoiceItems}
                />
              )}
            </div>
          ) : null}
          </div>
        );
      })}
      <button type="button" className="secondary" style={{ marginTop: 10 }} onClick={() => onChange([...blocks, newBlock()])}>
        + Add payment line
      </button>
      {showAccount ? (
        <CustomerAccountModal
          customerId={invoice.customerId}
          customerName={invoice.customerName}
          onClose={() => setShowAccount(false)}
          onChanged={setAccount}
        />
      ) : null}
    </div>
  );
};

/** Record payments against invoices (legacy payment.aspx). */
const PaymentEntry = () => {
  const { salesmen, beats, deliveryPersons, banks } = useReceivableLookups(["salesmen", "beats", "deliveryPersons", "banks"]);
  const [mode, setMode] = useState("single");
  const [customer, setCustomer] = useState(null);
  // salesman / beats narrowing the customer search (single customer mode)
  const [customerScope, setCustomerScope] = useState({ salesmanUserId: "", beatIds: [] });
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [salesmanUserId, setSalesmanUserId] = useState("");
  const [beatId, setBeatId] = useState("");
  const [deliveryUserId, setDeliveryUserId] = useState("");
  const [fromDate, setFromDate] = useState(todayIso());
  const [toDate, setToDate] = useState(todayIso());
  const [collectedByUserId, setCollectedByUserId] = useState("");
  const [invoices, setInvoices] = useState([]);
  // invoice being fully cancelled / returned (credit note + e-way bill and e-invoice cancellation)
  const [reversalInvoice, setReversalInvoice] = useState(null);
  const [blocksByInvoice, setBlocksByInvoice] = useState({});
  const [received, setReceived] = useState([]);
  const [importFile, setImportFile] = useState(null);
  // photo of the closed shop (Base64), sent with the submission when a line is Shop Close; stored in Azure Blob Storage
  const [shopPhoto, setShopPhoto] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  // receivable setting: a credit note line takes a reference number here (and nowhere else)
  const [creditNoteReference, setCreditNoteReference] = useState(false);
  const { products, warehouses } = useReturnLookups();
  // each selected bill's line items, once loaded for an item-wise credit note (for totals and validation)
  const [itemsByInvoice, setItemsByInvoice] = useState({});

  useEffect(() => {
    api
      .get(`${RECEIVABLES_API}/settings`)
      .then((settings) => setCreditNoteReference(Boolean(settings?.creditNoteReferenceRequired)))
      .catch(() => setCreditNoteReference(false));
  }, []);

  const reset = () => {
    setInvoices([]);
    setBlocksByInvoice({});
    setReceived([]);
    setImportResult(null);
  };

  const run = async (work) => {
    setLoading(true);
    try {
      await work();
    } catch (error) {
      toast.error(error.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = () => {
    if (mode === "single") {
      if (!customer && !invoiceNumber.trim()) throw new Error("Choose a customer or enter an invoice number");
      return api.get(`${PAYMENTS_API}/customer-invoices`, { customerId: customer?.id, invoiceNumber: invoiceNumber.trim() });
    }
    if (mode === "multiple") {
      if (!salesmanUserId && !beatId) throw new Error("Choose a salesman or a beat");
      return api.get(`${PAYMENTS_API}/salesman-invoices`, { salesmanUserId, beatId });
    }
    if (!deliveryUserId) throw new Error("Choose a delivery person");
    return api.get(`${PAYMENTS_API}/delivery-invoices`, { deliveryUserId, fromDate, toDate });
  };

  const loadInvoices = () =>
    run(async () => {
      const data = await fetchInvoices();
      setInvoices(Array.isArray(data) ? data : []);
      setBlocksByInvoice({});
      setReceived([]);
      if (!data?.length) toast.info("No invoices found");
    });

  const toggleInvoice = (invoice) =>
    setBlocksByInvoice((prev) => {
      const next = { ...prev };
      if (next[invoice.invoiceId]) delete next[invoice.invoiceId];
      else next[invoice.invoiceId] = [newBlock(Number(invoice.balanceAmount) > 0 ? String(invoice.balanceAmount) : "")];
      return next;
    });

  const selectedInvoices = invoices.filter((invoice) => blocksByInvoice[invoice.invoiceId]);
  const grandTotal = selectedInvoices.reduce(
    (total, invoice) =>
      total + sumBy(blocksByInvoice[invoice.invoiceId], (b) => amountOnBill(b, itemsByInvoice[invoice.invoiceId] || [])),
    0
  );

  const loadReceived = async (invoiceIds) => {
    if (!invoiceIds.length) return;
    const data = await api.get(`${PAYMENTS_API}/received-today`, { invoiceIds: invoiceIds.join(",") });
    setReceived(Array.isArray(data) ? data : []);
  };

  const submit = () =>
    run(async () => {
      if (selectedInvoices.length === 0) throw new Error("Select at least one invoice");
      const collector = mode === "delivery" ? deliveryUserId : collectedByUserId;
      for (const invoice of selectedInvoices) {
        for (const block of blocksByInvoice[invoice.invoiceId]) {
          if (!itemWise(block)) continue;
          const problem = validateReturnLines(block.returnLines || [], itemsByInvoice[invoice.invoiceId] || [], {
            checkReturnable: !isDebitNote(block),
          });
          if (problem) throw new Error(`${invoice.invoiceNumber}: ${problem}`);
        }
      }
      const entries = selectedInvoices.map((invoice) => ({
        invoiceId: invoice.invoiceId,
        collectedByUserId: collector ? Number(collector) : null,
        collections: blocksByInvoice[invoice.invoiceId].map((block) => {
          const f = fieldsFor(block.collectionType, creditNoteReference);
          const picked = isNote(block) ? block.creditNoteKind || "ITEM_WISE" : null;
          const kind = picked === "AMOUNT" ? null : picked;
          return {
            collectionType: block.collectionType,
            amount: kind === "ITEM_WISE"
              ? Math.round(returnLinesTotal(block.returnLines || [], itemsByInvoice[invoice.invoiceId] || []) * 100) / 100
              : f.amount ? Number(block.amount) : 0,
            referenceNumber: f.reference ? block.referenceNumber.trim() || null : null,
            bankName: f.bank ? block.bankName.trim() || null : null,
            branchName: f.branch ? block.branchName.trim() || null : null,
            chequeDate: f.instrumentDate ? block.chequeDate || null : null,
            paymentDate: block.paymentDate || null,
            creditNoteKind: kind,
            returnLines: kind === "ITEM_WISE" ? toReturnLineRequests(block.returnLines || []) : null,
          };
        }),
      }));
      const shopClosed = selectedInvoices.some((invoice) => blocksByInvoice[invoice.invoiceId].some((b) => b.collectionType === "SHOP_CLOSE"));
      const result = await api.post(PAYMENTS_API, {
        source: mode === "delivery" ? "DELIVERY" : "MANUAL",
        entries,
        shopCloseImage: shopClosed ? shopPhoto?.data || null : null,
      });
      setShopPhoto(null);
      const toAccountCount = selectedInvoices.reduce((n, invoice) => n + blocksByInvoice[invoice.invoiceId].filter(toAccount).length, 0);
      toast.success(
        `${result.saved} payment line(s) saved${toAccountCount ? `; ${toAccountCount} note(s) entered on the customer account balance` : ""}`
      );
      const ids = selectedInvoices.map((i) => i.invoiceId);
      setBlocksByInvoice({});
      const refreshed = await fetchInvoices();
      setInvoices(Array.isArray(refreshed) ? refreshed : []);
      await loadReceived(ids);
    });

  const uploadExcel = () =>
    run(async () => {
      if (!importFile) throw new Error("Choose an Excel file");
      const result = await api.uploadFile(`${PAYMENTS_API}/import`, importFile);
      setImportResult(result);
      if (result.imported) toast.success(`${result.imported} payment(s) imported`);
      if (result.skipped) toast.warn(`${result.skipped} row(s) skipped — see the list below`);
    });

  const beatOptions = salesmanUserId ? beats.filter((b) => String(b.salesmanUserId) === String(salesmanUserId)) : beats;
  const allSelected = invoices.length > 0 && invoices.every((i) => blocksByInvoice[i.invoiceId]);

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} />

      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginBottom: 12 }}>
        {[
          ["single", "Single customer"],
          ["multiple", "Multiple customers"],
          ["delivery", "Delivery"],
          ["excel", "Excel upload"],
        ].map(([value, label]) => (
          <label key={value} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <input
              type="radio"
              name="payment-mode"
              checked={mode === value}
              onChange={() => {
                setMode(value);
                reset();
              }}
            />
            {label}
          </label>
        ))}
      </div>

      {mode === "excel" ? (
        <div className="page-panel" style={{ maxWidth: 820 }}>
          <div className="page-subtitle" style={{ marginBottom: 10 }}>
            Columns: <strong>CustomerName, InvNumber, Type, Amount</strong> and optionally RefNumber (or ChequeNo), ChequeDate,
            BankName, BranchName, PaymentDate. Type is one of Cash, UPI, Cheque, E-Fund Transfer, Credit Note, Debit Note,
            Discount, Expired, FC Payout, No Payment, Delivered, Shop Close.
          </div>
          <div className="filters-bar">
            <input type="file" accept=".xlsx,.xls" onChange={(e) => setImportFile(e.target.files?.[0] || null)} />
            <button className="primary" onClick={uploadExcel} disabled={!importFile}>
              Upload
            </button>
          </div>
          {importResult ? (
            <div style={{ marginTop: 12 }}>
              <div>
                Imported <strong>{importResult.imported}</strong>, skipped <strong>{importResult.skipped}</strong>.
              </div>
              {importResult.errors?.length ? (
                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Problem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.errors.map((err) => (
                        <tr key={err.row}>
                          <td>{err.row}</td>
                          <td style={{ whiteSpace: "normal" }}>{err.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <div className="filters-bar">
            {mode === "single" ? (
              <>
                <CustomerScopeFilters
                  scope={customerScope}
                  onChange={(scope) => {
                    setCustomerScope(scope);
                    setCustomer(null);
                  }}
                  salesmen={salesmen}
                  beats={beats}
                  compact
                />
                <CustomerPicker
                  value={customer}
                  onChange={setCustomer}
                  salesmanUserId={customerScope.salesmanUserId || undefined}
                  beatIds={customerScope.beatIds}
                />
                <input
                  type="text"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="Invoice no (optional)"
                />
              </>
            ) : null}
            {mode === "multiple" ? (
              <>
                <select
                  value={salesmanUserId}
                  onChange={(e) => {
                    setSalesmanUserId(e.target.value);
                    setBeatId("");
                  }}
                  aria-label="Salesman"
                >
                  <option value="">-- Salesman --</option>
                  {salesmen.map((s) => (
                    <option key={s.userId} value={s.userId}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <select value={beatId} onChange={(e) => setBeatId(e.target.value)} aria-label="Beat">
                  <option value="">All beats</option>
                  {beatOptions.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </>
            ) : null}
            {mode === "delivery" ? (
              <>
                <select value={deliveryUserId} onChange={(e) => setDeliveryUserId(e.target.value)} aria-label="Delivery person">
                  <option value="">-- Delivery person --</option>
                  {deliveryPersons.map((p) => (
                    <option key={p.userId} value={p.userId}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} aria-label="From date" />
                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} aria-label="To date" />
              </>
            ) : null}
            <button className="primary" onClick={loadInvoices}>
              Show Invoices
            </button>
          </div>

          <HighlightLegend />
          <div className="table-wrapper" style={{ maxHeight: "45vh", overflowY: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() =>
                        setBlocksByInvoice(
                          allSelected
                            ? {}
                            : Object.fromEntries(
                                invoices.map((i) => [i.invoiceId, blocksByInvoice[i.invoiceId] || [newBlock(String(i.balanceAmount))]])
                              )
                        )
                      }
                      aria-label="Select all invoices"
                    />
                  </th>
                  <th>Customer</th>
                  <th>Invoice #</th>
                  <th>Invoice Date</th>
                  <th>Due Date</th>
                  <th style={{ textAlign: "right" }}>Invoice Amt</th>
                  <th style={{ textAlign: "right" }}>Balance</th>
                  <th>Remarks</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <EmptyRow colSpan={9} title="No invoices loaded" />
                ) : (
                  invoices.map((invoice) => (
                    <tr key={invoice.invoiceId} style={HIGHLIGHT_STYLES[invoice.flags?.highlight] || undefined}>
                      <td>
                        <input
                          type="checkbox"
                          checked={Boolean(blocksByInvoice[invoice.invoiceId])}
                          onChange={() => toggleInvoice(invoice)}
                        />
                      </td>
                      <td title={invoice.customerName}>{invoice.customerName}</td>
                      <td>{invoice.invoiceNumber}</td>
                      <td>{fmtDate(invoice.invoiceDate)}</td>
                      <td>{fmtDate(invoice.dueDate)}</td>
                      <td style={{ textAlign: "right" }}>{money(invoice.invoiceAmount)}</td>
                      <td style={{ textAlign: "right" }}>{money(invoice.balanceAmount)}</td>
                      <td title={invoice.flags?.remarks}>{invoice.flags?.remarks || "-"}</td>
                      <td className="action-buttons">
                        <button
                          className="secondary"
                          onClick={() => setReversalInvoice(invoice)}
                          title="Full return or full cancellation: credit note + cancel e-way bill and e-invoice"
                        >
                          Full Return
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {selectedInvoices.length ? (
            <>
              {selectedInvoices.map((invoice) => (
                <CollectionBlocks
                  key={invoice.invoiceId}
                  invoice={invoice}
                  blocks={blocksByInvoice[invoice.invoiceId]}
                  banks={banks}
                  creditNoteReference={creditNoteReference}
                  products={products}
                  warehouses={warehouses}
                  onItemsLoaded={(invoiceId, items) => setItemsByInvoice((prev) => ({ ...prev, [invoiceId]: items }))}
                  onChange={(blocks) => setBlocksByInvoice((prev) => ({ ...prev, [invoice.invoiceId]: blocks }))}
                />
              ))}
              <div className="filters-bar" style={{ marginTop: 14, justifyContent: "flex-end" }}>
                {mode !== "delivery" ? (
                  <select value={collectedByUserId} onChange={(e) => setCollectedByUserId(e.target.value)} aria-label="Collected by">
                    <option value="">Collected by: beat salesman</option>
                    {salesmen.map((s) => (
                      <option key={s.userId} value={s.userId}>
                        Collected by: {s.name}
                      </option>
                    ))}
                  </select>
                ) : null}
                {selectedInvoices.some((invoice) => blocksByInvoice[invoice.invoiceId].some((b) => b.collectionType === "SHOP_CLOSE")) ? (
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }} title="Photo of the closed shop (JPEG / PNG, up to 5 MB)">
                    Shop photo
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      capture="environment"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return setShopPhoto(null);
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error("The photo is larger than 5 MB");
                          e.target.value = "";
                          return undefined;
                        }
                        const reader = new FileReader();
                        reader.onload = () => setShopPhoto({ name: file.name, data: reader.result });
                        reader.readAsDataURL(file);
                        return undefined;
                      }}
                    />
                    {shopPhoto ? <img src={shopPhoto.data} alt="Shop" style={{ maxHeight: 40, borderRadius: 4 }} /> : null}
                  </label>
                ) : null}
                <strong>Grand Total: ₹{money(grandTotal)}</strong>
                <button className="primary" onClick={submit}>
                  Submit All
                </button>
              </div>
            </>
          ) : null}

          {received.length ? (
            <>
              <h3 style={{ margin: "20px 0 0", fontWeight: 600 }}>Collections received today</h3>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Receipt</th>
                      <th>Invoice</th>
                      <th>Customer</th>
                      <th>Type</th>
                      <th style={{ textAlign: "right" }}>Amount</th>
                      <th>Reference</th>
                      <th>Bank</th>
                      <th>Collected By</th>
                      <th>Collected At</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {received.map((row) => (
                      <tr key={row.id}>
                        <td>{row.receiptNumber}</td>
                        <td>{row.invoiceNumber}</td>
                        <td>{row.customerName}</td>
                        <td>{row.collectionTypeLabel}</td>
                        <td style={{ textAlign: "right" }}>{money(row.amount)}</td>
                        <td>{row.referenceNumber || "-"}</td>
                        <td>{row.bankName || "-"}</td>
                        <td>{row.collectedByName || "-"}</td>
                        <td>{fmtDateTime(row.collectedAt)}</td>
                        <td>
                          {row.approvalRequired && !row.approved ? (
                            <span className="badge badge-amber">Pending approval</span>
                          ) : (
                            <span className="badge badge-green">Posted</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </>
      )}
      {reversalInvoice ? (
        <FullReversalModal
          invoice={{ ...reversalInvoice, id: reversalInvoice.invoiceId }}
          onClose={() => setReversalInvoice(null)}
          onDone={loadInvoices}
        />
      ) : null}
    </div>
  );
};

export default PaymentEntry;
